/**
 * Leads API
 * ==========
 * API functions for lead management
 */

import apiClient from './client';

const LEADS_BASE = '/api/v1/leads';

/**
 * Create a new lead
 */
export const createLead = async (leadData) => {
  const response = await apiClient.post(LEADS_BASE, leadData);
  return response.data;
};

/**
 * List leads with filtering and pagination
 */
export const listLeads = async (params = {}) => {
  // Map frontend filter names to backend query parameter names
  const mappedParams = { ...params };
  if ('status' in mappedParams) {
    mappedParams.lead_status = mappedParams.status;
    delete mappedParams.status;
  }

  // Remove empty string values so they aren't sent as query params
  const cleanParams = Object.fromEntries(
    Object.entries(mappedParams).filter(([, v]) => v !== '' && v != null)
  );

  const response = await apiClient.get(LEADS_BASE, { params: cleanParams });
  return response.data;
};

/**
 * Get lead details by ID
 */
export const getLead = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}`);
  return response.data;
};

/**
 * Update lead information
 *
 * @param {string} leadId - Lead UUID
 * @param {Object} updateData - Lead fields to update
 * @param {string} [updateData.name] - Lead name
 * @param {string} [updateData.designation] - Lead designation
 * @param {string} [updateData.company] - Company name
 * @param {string} [updateData.email] - Email address
 * @param {string} [updateData.phone] - Phone number
 * @param {string} [updateData.website] - Website URL
 * @param {string} [updateData.linkedin] - LinkedIn URL
 * @param {string} [updateData.notes] - Notes
 * @param {string} [updateData.lead_source] - Lead source
 * @param {boolean} [updateData.trigger_research_refresh=false] - Trigger AI research
 *   regeneration. Dormant since 2026-09-07: no portal screen sends it true.
 * @returns {Promise<Object>} Updated lead data with changed_fields and research_regeneration_triggered
 * @throws {Error} 400 - Validation error, 403 - Permission denied, 404 - Lead not found
 */
export const updateLead = async (leadId, updateData) => {
  const response = await apiClient.put(`${LEADS_BASE}/${leadId}`, updateData);
  return response.data;
};

/**
 * Update lead status
 */
export const updateLeadStatus = async (leadId, statusData) => {
  const response = await apiClient.patch(`${LEADS_BASE}/${leadId}/status`, statusData);
  return response.data;
};

/**
 * Get AI-generated research for a lead
 */
export const getLeadResearch = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/research`);
  return response.data;
};

/**
 * Refresh lead research
 */
export const refreshLeadResearch = async (leadId) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/research/refresh`);
  return response.data;
};

/**
 * Get all research versions for a lead
 *
 * Returns a list of all research versions with metadata including:
 * - Version number
 * - Creation/update timestamps
 * - Regeneration trigger (lead_created, lead_updated, manual_refresh)
 * - Changed fields (for lead_updated trigger)
 * - Confidence scores
 * - Current version indicator
 *
 * @param {string} leadId - Lead UUID
 * @returns {Promise<Object>} Object containing lead_id, total_versions, and versions array
 * @throws {Error} 404 - Lead not found
 */
export const getResearchVersions = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/research/versions`);
  return response.data;
};

/**
 * Get a specific research version
 *
 * Retrieves the complete research data for a specific version number.
 * Version 1 is the initial research, subsequent versions are regenerations.
 *
 * @param {string} leadId - Lead UUID
 * @param {number} version - Version number to retrieve
 * @returns {Promise<Object>} Object containing lead_id, version, and complete research data
 * @throws {Error} 404 - Lead or version not found
 */
export const getResearchVersion = async (leadId, version) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/research/versions/${version}`);
  return response.data;
};

/**
 * Get management visibility analytics for a lead
 *
 * Returns qualification time, system processing time, estimated AI cost,
 * and tier classification.
 *
 * @param {string} leadId - Lead UUID
 * @returns {Promise<Object>} Analytics data with qualification_time, system_processing_time, estimated_cost, tier
 */
export const getLeadAnalytics = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/analytics`);
  return response.data;
};

/**
 * Schedule a meeting for a lead
 */
export const scheduleMeeting = async (leadId, meetingData) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/meetings`, meetingData);
  return response.data;
};

/**
 * Get all meetings for a lead
 */
export const getLeadMeetings = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/meetings`);
  return response.data;
};

/**
 * Get all emails for a lead
 */
export const getLeadEmails = async (leadId) => {
  const response = await apiClient.get(`${LEADS_BASE}/${leadId}/emails`);
  return response.data;
};

/**
 * Get a single meeting by ID
 */
export const getMeeting = async (meetingId) => {
  const response = await apiClient.get(`/api/v1/leads/meetings/${meetingId}`);
  return response.data;
};

/**
 * Update meeting status
 */
export const updateMeetingStatus = async (meetingId, statusData) => {
  const response = await apiClient.patch(`/api/v1/leads/meetings/${meetingId}/status`, statusData);
  return response.data;
};

/**
 * Manually fetch transcript from tl;dv for a meeting
 */
export const fetchTranscriptManually = async (leadId, meetingId) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/meetings/${meetingId}/fetch-transcript`);
  return response.data;
};

/**
 * Save manually entered transcript for a meeting
 */
export const saveManualTranscript = async (leadId, meetingId, transcriptData) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/meetings/${meetingId}/manual-transcript`, transcriptData);
  return response.data;
};

/**
 * Generate personalized questionnaire
 */
export const generateQuestionnaire = async (leadId, requestData = {}) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/questionnaire/generate`, requestData);
  return response.data;
};

/**
 * Remove a person image from lead research
 *
 * @param {string} leadId - Lead UUID
 * @param {number} imageIndex - Index of the image to remove (0-based)
 * @returns {Promise<Object>} Updated research data with image removed
 * @throws {Error} 400 - Invalid image index, 403 - Permission denied, 404 - Lead not found
 */
export const removeResearchImage = async (leadId, imageIndex) => {
  const response = await apiClient.delete(`${LEADS_BASE}/${leadId}/research/images/${imageIndex}`);
  return response.data;
};

/**
 * Get questionnaire for a lead
 *
 * @param {string} leadId - Lead UUID
 * @returns {Promise<Object>} Questionnaire data with all sections and responses.
 *   If questionnaire not yet generated, returns { completion_status: { status: 'not_generated' }, sections: [], ... }
 * @throws {Error} 404 - Lead not found
 */
export const getLeadQuestionnaire = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/questionnaire`);
  return response.data;
};

/**
 * Get questionnaire templates (structure)
 *
 * @returns {Promise<Object>} Questionnaire structure with all sections and questions
 */
export const getQuestionnaireTemplates = async () => {
  const response = await apiClient.get('/api/v1/questionnaire-templates');
  return response.data;
};

/**
 * Generate questionnaire responses from meeting transcript
 *
 * Triggers AI-powered extraction of questionnaire answers from the meeting transcript.
 * Will either generate initial responses or update existing ones.
 *
 * @param {string} leadId - Lead UUID
 * @param {string} meetingId - Meeting UUID
 * @returns {Promise<Object>} Generation result with questions_generated, questions_updated, conflicts_detected
 * @throws {Error} 400 - No transcript or no engagement, 404 - Meeting not found
 */
export const generateQuestionnaireFromMeeting = async (leadId, meetingId) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/meetings/${meetingId}/generate-questionnaire`);
  return response.data;
};

export const reprocessLeadEmail = async (leadId, emailId) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/emails/${emailId}/reprocess`, {});
  return response.data;
};

export const reprocessMeetingNotes = async (leadId, meetingId) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/meetings/${meetingId}/notes/reprocess`, {});
  return response.data;
};

export const reprocessMeetingTranscript = async (leadId, meetingId) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/meetings/${meetingId}/transcript/reprocess`, {});
  return response.data;
};

/**
 * Get questionnaire processing status for a lead
 *
 * Used for auto-polling during AI questionnaire generation.
 * Returns whether processing is in progress, stuck, completed, or failed.
 *
 * @param {string} leadId - Lead UUID
 * @returns {Promise<Object>} Processing status with fields:
 *   - is_processing: boolean - Whether AI is currently processing
 *   - status: string - "processing", "completed", "failed", "stuck", or "not_started"
 *   - should_poll: boolean - Whether frontend should continue polling
 *   - poll_interval_ms: number - Recommended polling interval in milliseconds
 *   - message: string - Human-readable status message
 *   - last_error: string|null - Error message if failed
 *   - processing_started_at: string|null - ISO timestamp when processing started
 * @throws {Error} 404 - Lead not found
 */
export const getQuestionnaireProcessingStatus = async (leadId) => {
  const response = await apiClient.get(`/api/v1/leads/${leadId}/questionnaire/processing-status`);
  return response.data;
};

/**
 * Regenerate questionnaire for a lead
 *
 * Manually triggers questionnaire regeneration from the latest meeting transcript.
 * Use this to retry when processing appears stuck or failed.
 *
 * @param {string} leadId - Lead UUID
 * @returns {Promise<Object>} Result with message, lead_id, meeting_id, and status: "processing"
 * @throws {Error} 404 - Lead or meeting not found
 */
export const regenerateQuestionnaire = async (leadId) => {
  const response = await apiClient.post(`/api/v1/leads/${leadId}/questionnaire/regenerate`, {});
  return response.data;
};

/**
 * Record a telephonic conversation directly against a lead
 *
 * Creates a completed LeadMeeting with type=telephonic_conversation and
 * triggers the same questionnaire-fill pipeline used for scheduled meetings.
 *
 * @param {string} leadId - Lead UUID
 * @param {Object} data
 * @param {string} [data.title] - Optional title; server defaults to "Telephonic Conversation - {lead.name}"
 * @param {string} [data.conversation_date] - ISO datetime string; server defaults to now
 * @param {number} [data.duration_minutes=15] - Duration in minutes (1-600)
 * @param {string[]} [data.participants] - Optional attendee emails
 * @param {string} data.notes - Required conversation notes (min 10 chars)
 * @returns {Promise<Object>} { meeting_id, title, scheduled_at, transcript_status, summary, questionnaire_generation_triggered }
 * @throws {Error} 400 - Validation, 403 - Permission denied, 404 - Lead not found
 */
export const recordTelephonicConversation = async (leadId, data) => {
  const response = await apiClient.post(`${LEADS_BASE}/${leadId}/conversations`, data);
  return response.data;
};

/**
 * Fetch the full extracted text for one email attachment (lazy preview).
 */
export const getEmailAttachmentText = async (leadId, emailId, attachmentId) => {
  const response = await apiClient.get(
    `${LEADS_BASE}/${leadId}/emails/${emailId}/attachments/${attachmentId}`
  );
  return response.data;
};

/**
 * Re-run the attachment orchestrator for one email (e.g., after a user connects Drive).
 */
export const retryEmailAttachments = async (leadId, emailId) => {
  const response = await apiClient.post(
    `${LEADS_BASE}/${leadId}/emails/${emailId}/retry-attachments`
  );
  return response.data;
};

/**
 * Manually upload a document for an email. Optionally supersedes an existing attachment row.
 * @param {string} leadId
 * @param {string} emailId
 * @param {File} file
 * @param {Object} [options]
 * @param {string} [options.supersedesAttachmentId] - If replacing a failed/blocked automatic row
 * @param {string} [options.note] - Optional human note
 */
export const uploadEmailAttachment = async (leadId, emailId, file, options = {}) => {
  const form = new FormData();
  form.append('file', file);
  if (options.supersedesAttachmentId) {
    form.append('supersedes_attachment_id', options.supersedesAttachmentId);
  }
  if (options.note) {
    form.append('note', options.note);
  }
  const response = await apiClient.post(
    `${LEADS_BASE}/${leadId}/emails/${emailId}/attachments/upload`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

const leadAPI = {
  createLead,
  listLeads,
  getLead,
  updateLead,
  updateLeadStatus,
  getLeadResearch,
  getLeadAnalytics,
  refreshLeadResearch,
  getResearchVersions,
  getResearchVersion,
  scheduleMeeting,
  getLeadMeetings,
  getLeadEmails,
  getMeeting,
  updateMeetingStatus,
  fetchTranscriptManually,
  saveManualTranscript,
  generateQuestionnaire,
  removeResearchImage,
  getLeadQuestionnaire,
  getQuestionnaireTemplates,
  generateQuestionnaireFromMeeting,
  getQuestionnaireProcessingStatus,
  regenerateQuestionnaire,
  recordTelephonicConversation,
  getEmailAttachmentText,
  retryEmailAttachments,
  uploadEmailAttachment,
  reprocessLeadEmail,
  reprocessMeetingNotes,
  reprocessMeetingTranscript,
};

export default leadAPI;

